import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Users, Database, Globe, MessageCircle, Mail, Briefcase } from 'lucide-react';
import MarketingLayout from '../../components/Marketing/MarketingLayout';

const LandingPage = () => {
  const navigate = useNavigate();

  const featureBlockStyle = {
    background: '#ffffff',
    padding: '32px',
    borderRadius: '24px',
    border: '1px solid #f1f5f9',
    transition: 'all 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  };

  return (
    <MarketingLayout 
      title="CRM Automax | Lead Management, WhatsApp & Email Automation CRM"
      metaDescription="CRM Automax helps businesses manage leads, scrape & upload leads in bulk, automate WhatsApp messaging (API + Web), and run bulk email campaigns — all in one affordable CRM."
    >
      {/* ── Hero Section ── */}
      <section className="marketing-hero-section" style={{
        padding: '100px 48px 120px', display: 'flex', flexDirection: 'column',
        alignItems: 'center', textAlign: 'center', overflow: 'hidden',
        background: 'radial-gradient(circle at top right, #eff6ff 0%, transparent 40%), radial-gradient(circle at bottom left, #f5f3ff 0%, transparent 40%)'
      }}>
        <h1 className="animate-fade-up" style={{ 
          fontSize: 'clamp(40px, 8vw, 64px)', fontWeight: '900', color: '#1e293b', 
          lineHeight: 1.1, letterSpacing: '-0.04em', maxWidth: '900px', 
          marginBottom: '24px'
        }}>
          Manage Leads. Automate WhatsApp. Send Bulk Emails. <span style={{ color: '#3b82f6' }}>All in One Place.</span>
        </h1>
        
        <p className="animate-fade-up" style={{ 
          fontSize: '20px', color: '#64748b', maxWidth: '750px', 
          lineHeight: 1.6, marginBottom: '48px', animationDelay: '0.1s'
        }}>
          CRM Automax is built for agencies and businesses that need to capture, manage, and convert leads faster — without paying enterprise prices.
        </p>

        <div className="animate-fade-up" style={{ display: 'flex', gap: '16px', animationDelay: '0.2s', flexWrap: 'wrap', justifyContent: 'center' }}>
          <button onClick={() => navigate('/register')} style={{
            background: '#3b82f6', color: 'white', border: 'none',
            padding: '18px 36px', borderRadius: '14px', fontSize: '16px',
            fontWeight: '700', cursor: 'pointer', display: 'flex', 
            alignItems: 'center', gap: '10px', boxShadow: '0 10px 15px -3px rgba(59, 130, 246, 0.3)'
          }}>
            Get Started Free <ArrowRight size={18} />
          </button>
          <button onClick={() => navigate('/pricing')} style={{
            background: 'white', color: '#1e293b', border: '1px solid #e2e8f0',
            padding: '18px 36px', borderRadius: '14px', fontSize: '16px',
            fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s ease',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
          }}>
            See Pricing
          </button>
        </div>


      </section>

      {/* ── What You Can Do ── */}
      <section className="marketing-content-section" style={{ padding: '120px 48px', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '80px' }}>
          <h2 style={{ fontSize: '40px', fontWeight: '800', marginBottom: '16px' }}>What You Can Do With CRM Automax</h2>
          <p style={{ fontSize: '18px', color: '#64748b', maxWidth: '700px', margin: '0 auto' }}>
            Whether you're a solo founder or a large agency with thousands of leads, CRM Automax adapts to how you work.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '32px' }}>
          {[
            { 
              icon: <Users color="#3b82f6" />, 
              title: "Smart Lead Management", 
              desc: "Capture, organize, and track every lead in one centralized dashboard. Add leads manually, generate them automatically, or bring in your existing database — nothing gets lost, nothing gets duplicated." 
            },
            { 
              icon: <Database color="#10b981" />, 
              title: "Bulk Lead Upload for Agencies", 
              desc: "Already have thousands of leads sitting in spreadsheets? Upload them in bulk in seconds and get straight to managing and converting — no manual data entry required." 
            },
            { 
              icon: <Globe color="#f59e0b" />, 
              title: "Built-in Lead Scraper", 
              desc: "Don't have a lead list yet? Use our integrated lead scraper to discover and pull fresh leads directly into your CRM, ready to organize and contact." 
            },
            { 
              icon: <MessageCircle color="#8b5cf6" />, 
              title: "WhatsApp API + WhatsApp Web, Together", 
              desc: "Connect your official WhatsApp Business API for automated, scalable messaging — or use WhatsApp Web directly inside the platform for quick, personal outreach. You get both, your choice." 
            },
            { 
              icon: <MessageCircle color="#0ea5e9" />, 
              title: "Bulk WhatsApp Messaging", 
              desc: "Send bulk WhatsApp messages to your entire lead list through the API or WhatsApp Web (Web JS) — perfect for promotions, follow-ups, and announcements at scale." 
            },
            { 
              icon: <Mail color="#f43f5e" />, 
              title: "Bulk Email Sender & Campaigns", 
              desc: "Design, schedule, and send bulk email campaigns directly from your CRM. Track performance and manage your entire outreach without switching tools." 
            }
          ].map((feature, i) => (
            <div key={i} className="feature-card" style={featureBlockStyle}>
              <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: '#f8fafc', border: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {feature.icon}
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: '700' }}>{feature.title}</h3>
              <p style={{ fontSize: '15px', color: '#64748b', lineHeight: 1.6 }}>{feature.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Why Agencies Choose CRM Automax ── */}
      <section className="marketing-content-section" style={{ padding: '120px 48px', background: '#f8fafc' }}>
        <div className="marketing-flex-container" style={{ maxWidth: '1100px', margin: '0 auto', display: 'flex', gap: '80px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '320px' }}>
            <h2 style={{ fontSize: '40px', fontWeight: '800', marginBottom: '32px', lineHeight: 1.1 }}>Why Agencies Choose <span style={{ color: '#3b82f6' }}>CRM Automax</span></h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {[
                'One platform instead of five different tools',
                'Handle leads from upload, scraping, or manual entry — your choice',
                'WhatsApp API and WhatsApp Web both built in',
                'Bulk email + bulk WhatsApp from a single dashboard',
                'Affordable pricing built for growing teams and agencies'
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', fontWeight: '600', color: '#1e293b', fontSize: '18px' }}>
                  <CheckCircle2 size={24} color="#10b981" style={{ flexShrink: 0, marginTop: '2px' }} /> {item}
                </div>
              ))}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: '320px', position: 'relative' }}>
             <div style={{ 
               background: 'white', padding: '40px', borderRadius: '32px', 
               border: '1px solid #f1f5f9', boxShadow: '0 20px 40px rgba(0,0,0,0.03)',
               display: 'flex', flexDirection: 'column', gap: '24px'
             }}>
                <div style={{ width: '64px', height: '64px', borderRadius: '16px', background: 'rgba(59, 130, 246, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Briefcase size={32} color="#3b82f6" />
                </div>
                <h3 style={{ fontSize: '28px', fontWeight: '800', margin: 0 }}>Ready to bring all your leads and outreach into one place?</h3>
                <div style={{ display: 'flex', gap: '16px', marginTop: '16px', flexWrap: 'wrap' }}>
                  <button onClick={() => navigate('/pricing')} style={{
                    background: '#3b82f6', color: 'white', border: 'none',
                    padding: '16px 32px', borderRadius: '12px', fontWeight: '700', fontSize: '16px', cursor: 'pointer',
                    boxShadow: '0 10px 15px -3px rgba(59, 130, 246, 0.3)'
                  }}>
                    Start Now
                  </button>
                  <button onClick={() => navigate('/pricing')} style={{
                    background: 'transparent', color: '#1e293b', border: '1px solid #e2e8f0',
                    padding: '16px 32px', borderRadius: '12px', fontWeight: '700', fontSize: '16px', cursor: 'pointer'
                  }}>
                    View Pricing
                  </button>
                </div>
             </div>
          </div>
        </div>
      </section>

    </MarketingLayout>
  );
};

export default LandingPage;
